import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/controls/Modal";
import Button from "../../../components/controls/Button";
import FormInput from "../../../components/controls/FormInput";
import FormTextarea from "../../../components/controls/FormTextArea";
import CitySelector from "../../../components/dropdowns/CitySelector";

export default function EditCompanyModal({
  isOpen,
  onClose,
  company,
  onCompanyUpdated,
}) {
  const { t } = useTranslation();

  const [selectedCityId, setSelectedCityId] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !company) return;
    setSelectedCityId(company.cityId ?? null);
    setName(company.name ?? "");
    setNote(company.note ?? "");
  }, [isOpen, company]);

  const handleUpdate = async () => {
    if (!company) return;
    setSaving(true);

    try {
      const payload = {
        name,
        cityId: selectedCityId,
        createdBy: company.createdBy,
        assignedTo: company.assignedTo,
        note: note.trim() || null,
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact/${company.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Erreur lors de la mise à jour");
      }

      const updatedCompany = await res.json().catch(() => null);
      onCompanyUpdated?.(updatedCompany || { ...company, ...payload });
      onClose?.();
    } catch (err) {
      alert(err?.message || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button
        type="button"
        color="secondary"
        size="sm"
        onClick={onClose}
        disabled={saving}
      >
        {t("global.cancel")}
      </Button>
      <Button
        type="button"
        color="primary"
        size="sm"
        onClick={handleUpdate}
        disabled={saving}
      >
        {t("global.save")}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen && !!company}
      onClose={onClose}
      title={t("reservation.editCompany")}
      footer={footer}
      size="md"
      showCloseButton
    >
      {!company ? null : (
        <div className="text-primary space-y-4">
          <CitySelector value={selectedCityId} onChange={setSelectedCityId} />

          <FormInput
            name="name"
            label={t("reservation.companyName")}
            value={name}
            onChange={(field, value) => setName(value)}
            required
          />

          <FormTextarea
            value={note}
            label={t("global.note")}
            name="note"
            onChange={(field, value) => setNote(value)}
            placeholder={"Ex: # Téléphone, Email, Adresse, lien Facebook..."}
            rows={3}
            maxLength={500}
            showMaxLength
            required
          />
        </div>
      )}
    </Modal>
  );
}
